import React from "react";
import ReactDOM from "react-dom";
import "./profilemenu.scss";

// Shared profile avatar + dropdown used across the app (header + SmartHub) so the look is
// uniform in one place. Self-contained: derives the user from localStorage, renders the
// avatar, opens a dropdown on click, closes on outside-click/Escape. The caller supplies
// `onLogout` so each place keeps its own sign-out behaviour.
//
// The dropdown is rendered in a portal (document.body) with fixed positioning anchored to
// the avatar, so it is never clipped or stacked under a parent — e.g. the MUI AppBar, which
// has its own stacking context (z-index) and a fixed height that would otherwise hide it.
export default function ProfileMenu({ onLogout, className = "" }) {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState(null);
  const avatarRef = React.useRef(null);
  const dropRef = React.useRef(null);

  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch (_) { return {}; }
  })();
  const initials = (user.username || "U").substring(0, 2).toUpperCase();

  const place = () => {
    const el = avatarRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: Math.round(r.bottom + 8), right: Math.round(Math.max(8, window.innerWidth - r.right)) });
  };

  const toggle = () => { if (!open) place(); setOpen((p) => !p); };

  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (avatarRef.current && avatarRef.current.contains(e.target)) return;
      if (dropRef.current && dropRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const reposition = () => place();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open]);

  const dropdown = open && pos ? ReactDOM.createPortal(
    <div
      ref={dropRef}
      className="profile-menu-dropdown"
      style={{ position: "fixed", top: pos.top, right: pos.right }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="profile-menu-info">
        <div className="profile-menu-name">{user.username}</div>
        <div className="profile-menu-email">{user.email}</div>
      </div>
      <div className="profile-menu-divider" />
      <button
        type="button"
        className="profile-menu-logout"
        onClick={() => { setOpen(false); if (onLogout) onLogout(); }}
      >
        <span className="profile-menu-logout-glyph">⏻</span> Logout
      </button>
    </div>,
    document.body
  ) : null;

  return (
    <div className={`profile-menu ${className}`.trim()}>
      <button ref={avatarRef} type="button" className="profile-menu-avatar" onClick={toggle} title={user.username}>
        {initials}
      </button>
      {dropdown}
    </div>
  );
}
